import {ButtonHTMLAttributes, FC} from 'react';
import clsx from 'clsx';
import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import {IconColorType} from 'components/IconColor/types';
import Spinner from 'components/Loaders/Spinner';
import {useTooltip} from 'components/Tooltip/hook';
import {Size} from 'types';
import {noopPreventClick, noopPreventSubmit} from 'utils/functions';
import IconColor from '../IconColor';
import {
    destructive,
    primary,
    secondary,
    tertiary,
    tertiaryIconOnly,
} from './styles.module.css';

export type Kind =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'custom';

export type ButtonProps<T = HTMLButtonElement> = {
    icon?: IconType;
    iconColor?: IconColorType;
    isAlignedCenter?: boolean;
    isLoading?: boolean;
    kind?: Kind;
    size?: Size;
} & ButtonHTMLAttributes<T>;

export const SIZES: Record<Size, string> = {
    base: 'text-base py-2 px-3',
    lg: 'text-lg py-2 px-4',
    sm: 'text-sm py-1.5 px-2',
    xl: 'text-xl py-2.5 px-4',
    xs: 'text-sm py-0.5 px-1.5',
};

export const ICON_SIZES: Record<Size, string> = {
    base: 'text-base p-3',
    lg: 'text-lg p-3.5',
    sm: 'text-sm p-2',
    xl: 'text-xl p-4',
    xs: 'text-xs p-1',
};

export const KINDS: Record<Kind, string> = {
    custom: '',
    destructive,
    primary,
    secondary,
    tertiary,
};

export const ICON_MARGIN = {
    base: 'mr-2',
    lg: 'mr-2',
    sm: 'mr-2',
    xl: 'mr-2',
    xs: 'mr-1.5',
};

export const BASE_ICON = ['xs', 'sm', 'base', 'lg'];

const SPINNER_SIZES: Record<Size, string> = {
    base: 'h-4',
    lg: 'h-5',
    sm: 'h-3.5',
    xl: 'h-6',
    xs: 'h-3',
};

const Button: FC<ButtonProps> = ({
    children,
    className,
    disabled,
    icon,
    iconColor,
    isAlignedCenter = true,
    isLoading,
    kind = 'primary',
    onClick,
    onSubmit,
    size = 'base',
    type = 'button',
    ...rest
}) => {
    const {setTooltipRef} = useTooltip();

    const iconComponent = icon ? (
        <Icon
            className={clsx(
                children && 'flex-none',
                children && ICON_MARGIN[size],
            )}
            icon={icon}
            size={BASE_ICON.includes(size) ? 'base' : 'lg'}
        />
    ) : iconColor ? (
        <IconColor
            className={clsx(
                children && 'flex-none',
                children && ICON_MARGIN[size],
            )}
            icon={iconColor}
            size={BASE_ICON.includes(size) ? 'base' : 'lg'}
        />
    ) : null;

    return (
        <button
            ref={(ref) => {
                setTooltipRef(ref);
            }}
            className={clsx(
                'select-none whitespace-nowrap rounded border',
                KINDS[kind],
                !children && icon && tertiaryIconOnly,
                !iconComponent || children ? SIZES[size] : ICON_SIZES[size],
                className,
            )}
            data-disabled={disabled ? true : undefined}
            data-status={isLoading ? 'loading' : undefined}
            onClick={disabled ? noopPreventClick : onClick}
            onSubmit={disabled ? noopPreventSubmit : onSubmit}
            tabIndex={disabled ? -1 : undefined}
            type={type}
            {...rest}
        >
            {isLoading ? (
                <div className="relative">
                    <div
                        className={clsx(
                            isLoading && 'invisible',
                            iconComponent && children && 'flex items-center',
                        )}
                    >
                        {iconComponent}
                        {children}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Spinner
                            className={SPINNER_SIZES[size]}
                            id={kind}
                            size="xs"
                        />
                    </div>
                </div>
            ) : iconComponent && children ? (
                <div
                    className={clsx(
                        'flex items-center',
                        isAlignedCenter && 'justify-center',
                    )}
                >
                    {iconComponent}
                    {children}
                </div>
            ) : (
                <>
                    {iconComponent}
                    {children}
                </>
            )}
        </button>
    );
};

export default Button;
